import React, { useCallback, useState, useRef, useMemo } from 'react';
import clsx from 'clsx';
import { SelectIcon, Search, ClearIcon } from '@td-client-frontend/icons';
import { FieldWrapper } from './FieldWrapper';
import { TextInput } from './TextField';
import ItemsList from './SearchItemsList';
import { useEventListener } from '../../hooks';
import { FieldButtonWithIcon } from '../FieldButtonWithIcon';

const SEARCH_PLACEHOLDER = 'Please start typing...';

export interface SearchSelectItem {
  label: string;
  value: any;
  key?: any;
  items?: SearchSelectItem[];
}

export interface SearchSelectInputProps {
  buildTemplate?: (item: SearchSelectItem) => React.ReactElement;
  options?: null | Array<SearchSelectItem>;
  onSearchChange?: (search: string) => void;
  onSelect: (selectedItem: SearchSelectItem) => void;
  name: string;
  placeholder?: string;
  value: string | number;
  isError?: boolean;
  required?: boolean;
  dataIsLoading?: boolean;
  wrapperClasses?: string;
  isSearch?: boolean;
  isFiltered?: boolean;
  closeOnSelect?: boolean;
  onSearch?: (search: string) => void;
  maxVisible?: number;
  readOnly?: boolean;
  noSearchResultsMessage?: string;
  onKeyDown?: (e: any) => void;
  efficientValueLength?: number;
  onClear?: () => void;
  fieldClasses?: string;
  clearOnClose?: boolean;
}

export interface SearchSelectProps extends SearchSelectInputProps {
  label: string;
  labelBtn?: () => void;
  error?: string;
  fieldWrapperWidthClass?: string;
}

const SearchSelect = ({
  name,
  label,
  error = '',
  isError = false,
  labelBtn,
  fieldWrapperWidthClass,
  ...props
}: SearchSelectProps) => {
  return (
    <FieldWrapper
      label={label}
      error={error}
      isError={isError}
      name={name}
      labelBtn={labelBtn}
      className={clsx('flex flex-col mb-3', fieldWrapperWidthClass || 'w-full')}
    >
      <SearchSelectInput {...props} name={name} isError={isError} />
    </FieldWrapper>
  );
};

const SearchSelectInput = ({
  name,
  placeholder = SEARCH_PLACEHOLDER,
  isError,
  options = null,
  buildTemplate = (item) => <div>{item.label}</div>,
  onSearchChange,
  onSelect,
  value,
  wrapperClasses,
  required = true,
  dataIsLoading = false,
  isSearch = false,
  onSearch,
  maxVisible = 4,
  isFiltered = true,
  closeOnSelect = true,
  efficientValueLength = 3,
  readOnly = false,
  noSearchResultsMessage,
  onKeyDown,
  onClear,
  fieldClasses,
  clearOnClose = true
}: SearchSelectInputProps) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const searchInputRef = useRef<HTMLInputElement>(null);

  const availableOptions = useMemo(() => {
    if (!isFiltered || !options || !searchTerm || searchTerm.length < efficientValueLength) {
      return options;
    }
    const value = searchTerm.toLocaleLowerCase();

    const optionsReducer = (data: SearchSelectItem[]) => {
      return data.reduce((array: SearchSelectItem[], element: SearchSelectItem) => {
        if (element.label.toLocaleLowerCase().includes(value) && !element.items) {
          array.push(element);
        } else if (element.items && element.items.length > 0) {
          const subElements = optionsReducer(element.items);
          if (subElements.length) array.push({ ...element, items: subElements });
        }
        return array;
      }, []);
    };

    return optionsReducer(options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, searchTerm]);

  const handleItemSelect = useCallback(
    (item: SearchSelectItem) => {
      setSearchTerm(item.label);
      onSelect(item);
    },
    [onSelect]
  );

  const handleClear = useCallback(() => {
    setSearchTerm('');
    if (onClear) onClear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClose = useCallback(() => {
    setExpanded(false);
    if (
      clearOnClose &&
      availableOptions?.length &&
      !availableOptions.find(
        (option: SearchSelectItem) =>
          option?.value?.toString() === value?.toString() && option?.label?.toLowerCase() === searchTerm?.toLowerCase()
      )
    ) {
      // if selected value is messed in a search box, reset text field on closing drop down
      setSearchTerm('');
      onSearchChange && onSearchChange('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const selectedItem = useMemo(() => {
    const allOptions = availableOptions?.reduce(
      (array: Array<SearchSelectItem>, element: SearchSelectItem) => [
        ...array,
        ...(element.items?.length ? element.items : [element])
      ],
      []
    );

    const selectedOption = allOptions?.find(({ value: v }: SearchSelectItem) => v?.toString() === value?.toString());

    if (!searchTerm || searchTerm.length < efficientValueLength || !expanded) {
      setSearchTerm(selectedOption?.label || '');
    }
    return selectedOption;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, availableOptions]);

  const onInputChange = ({ target: { value: inputValue } }: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(inputValue);
    if (onSearchChange && inputValue.length >= efficientValueLength) {
      onSearchChange(inputValue);
      setExpanded(true);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onSelectIconClick = () => {
    setExpanded(!expanded);
  };

  const onTextInputClick = () => {
    setExpanded(!expanded);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearch = () => {
    if (onSearch && isSearch) {
      onSearch(searchTerm);
    }
  };

  const handleKeyDown = useCallback(
    (event) => {
      const { keyCode } = event;
      if (keyCode === 13) {
        handleSearch();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEventListener('keydown', handleKeyDown, searchInputRef.current);

  const Icon = useMemo(() => {
    return onClear && value ? (
      <FieldButtonWithIcon onClick={handleClear} icon={<ClearIcon className='fill-current' aria-hidden />} />
    ) : isSearch || !options ? (
      <button
        className='flex items-center px-4 self-stretch h-full focus:outline-none focus-visible:shadow-outline disabled:opacity-50 bg-primary-600'
        onClick={handleSearch}
        aria-label='Search'
        disabled={dataIsLoading}
      >
        <Search className='fill-white' aria-hidden />
      </button>
    ) : (
      <SelectIcon
        className={clsx('mr-3 cursor-pointer', expanded && !onClear && 'transform rotate-180')}
        onClick={onSelectIconClick}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearch, onClear, value, expanded]);

  return (
    <div className='relative' data-testid={`searchSelect-wrapper-${name}`}>
      <TextInput
        data-testid='searchSelect-text-input'
        name={name}
        placeholder={placeholder}
        onClick={onTextInputClick}
        value={searchTerm}
        onChange={onInputChange}
        ref={searchInputRef}
        fieldClasses={fieldClasses}
        wrapperClassName={clsx('relative', wrapperClasses)}
        Right={Icon}
        onKeyDown={onKeyDown}
        isError={isError}
        required={required}
        autoComplete='off'
        aria-haspopup
        aria-expanded={expanded}
        aria-activedescendant={`option-for-${value}`}
        aria-owns={`listbox-${name}`}
        aria-required={required}
        aria-invalid={isError}
        aria-describedby={`error-for-${name}`}
        role='combobox'
        readOnly={readOnly}
      />
      {availableOptions && (
        <ItemsList
          options={availableOptions}
          buildTemplate={buildTemplate}
          searchInput={searchInputRef}
          onItemSelected={handleItemSelect}
          handleClose={onClose}
          closeOnSelect={closeOnSelect}
          setExpanded={setExpanded}
          expanded={expanded}
          selectedItem={selectedItem}
          label='list-items-label'
          dataIsLoading={dataIsLoading}
          maxVisible={maxVisible}
          noSearchResultsMessage={noSearchResultsMessage}
        />
      )}
    </div>
  );
};

export { SearchSelect, SearchSelectInput };
